import { Coins, FlaskConical, Rocket, ScanSearch, Zap } from 'lucide-react';
import type { FormEvent } from 'react';
import { useId, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, buttonClasses } from '../components/ui/Button';
import { Notice, PageHeader } from '../components/ui/Feedback';
import { useToast } from '../components/ui/Toast';
import { useConfig, useCreateReview } from '../hooks/queries';
import { readPreferences } from '../hooks/usePreferences';
import { useMe } from '../hooks/useAuth';
import { ApiError } from '../services/api';
import { cn } from '../lib/cn';
import { validatePrUrl } from '../lib/validation';
import type { ReviewDepth } from '../types/api';

const FOCUS_MAX = 500;
const SAMPLE_STRIPES = ['border-l-chalk-pink', 'border-l-chalk-blue', 'border-l-chalk-orange'];

const DEPTHS: Array<{ id: ReviewDepth; title: string; description: string; icon: typeof Zap }> = [
  { id: 'quick', title: 'Quick', description: 'The most important issues only. Faster and cheaper.', icon: Zap },
  { id: 'thorough', title: 'Thorough', description: 'Examines every hunk. Slower, uses more tokens.', icon: ScanSearch },
];

export function NewReviewPage() {
  const [params] = useSearchParams();
  const [prUrl, setPrUrl] = useState(params.get('url') ?? '');
  const [focus, setFocus] = useState('');
  const [depth, setDepth] = useState<ReviewDepth>(() => readPreferences().defaultDepth);
  const [touched, setTouched] = useState(false);
  const create = useCreateReview();
  const { data: config } = useConfig();
  const { data: me } = useMe();
  const credits = me?.credits ?? 0;
  const outOfCredits = !!me && credits < 1;
  const navigate = useNavigate();
  const toast = useToast();
  const urlId = useId();
  const urlErrId = useId();
  const focusId = useId();

  const urlError = validatePrUrl(prUrl);
  const showUrlError = touched && urlError;
  const claudeReady = !!config?.claude.configured;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (urlError || outOfCredits) return;
    create.mutate(
      { prUrl: prUrl.trim(), depth, focus: focus.trim() || undefined },
      {
        onSuccess: ({ review, duplicate }) => {
          if (duplicate) toast('A review of this commit is already running — opened it instead.', 'info');
          navigate(`/app/reviews/${review.id}`);
        },
      },
    );
  };

  return (
    <div className="max-w-3xl">
      <PageHeader title="Start New Review" color="green" description="Review a GitHub Pull Request for potential bugs, security issues and edge cases." />

      {outOfCredits && (
        <Notice
          tone="danger"
          className="mb-5"
          title="You're out of review credits"
          action={
            <Link to="/app/credits" className={buttonClasses('primary', 'sm')}>
              <Coins className="h-3.5 w-3.5" aria-hidden />
              Add credits
            </Link>
          }
        >
          Each review uses one credit. Add credits to your account to review a Pull Request.
        </Notice>
      )}

      {config && !claudeReady && (
        <Notice tone="warn" className="mb-5" title="Real reviews need AI Pilot">
          {config.demoMode
            ? 'The server runs with DEMO_MODE=true, so only the sample PRs below can be reviewed.'
            : 'Add the AI API key and model to the server settings to review real Pull Requests. Until then you can try the sample PRs below.'}
        </Notice>
      )}

      <form onSubmit={onSubmit} noValidate className="card space-y-7 p-5 sm:p-7">
        <div>
          <label htmlFor={urlId} className="block text-sm font-medium">
            Pull Request URL
          </label>
          <input
            id={urlId}
            type="url"
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            placeholder="https://github.com/owner/repo/pull/123"
            value={prUrl}
            onChange={(e) => setPrUrl(e.target.value)}
            onBlur={() => setTouched(true)}
            aria-invalid={!!showUrlError}
            aria-describedby={showUrlError ? urlErrId : undefined}
            className={cn('input mt-1.5 font-mono', showUrlError && 'border-danger focus:border-danger focus:ring-danger/30')}
          />
          {showUrlError ? (
            <p id={urlErrId} className="mt-1.5 text-xs text-danger" role="alert">
              {urlError}
            </p>
          ) : (
            <p className="mt-1.5 text-xs text-faint">
              Public repositories work without a token. Private ones need a GITHUB_TOKEN on the server.
            </p>
          )}
        </div>

        <fieldset>
          <legend className="text-sm font-medium">Review depth</legend>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            {DEPTHS.map((d) => (
              <label
                key={d.id}
                className={cn(
                  'flex cursor-pointer gap-3 rounded-xl border-2 p-4 transition-colors',
                  depth === d.id ? 'border-chalk-green/70 bg-chalk-green/10' : 'border-line hover:bg-raised/60',
                )}
              >
                <input
                  type="radio"
                  name="depth"
                  value={d.id}
                  checked={depth === d.id}
                  onChange={() => setDepth(d.id)}
                  className="mt-1 accent-[rgb(var(--chalk-green))]"
                />
                <span>
                  <span className="flex items-center gap-1.5 text-sm font-medium">
                    <d.icon className={cn('h-4 w-4', d.id === 'quick' ? 'text-chalk-yellow' : 'text-chalk-lilac')} aria-hidden />
                    {d.title}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted">{d.description}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <div className="flex items-baseline justify-between">
            <label htmlFor={focusId} className="block text-sm font-medium">
              Review focus <span className="font-normal text-faint">(optional)</span>
            </label>
            <span className={cn('text-xs tabular-nums', focus.length > FOCUS_MAX ? 'text-danger' : 'text-faint')}>
              {focus.length}/{FOCUS_MAX}
            </span>
          </div>
          <textarea
            id={focusId}
            rows={3}
            maxLength={FOCUS_MAX}
            placeholder="e.g. Pay special attention to authorization checks and SQL queries."
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
            className="input mt-1.5 resize-y"
          />
        </div>

        {create.error && (
          <Notice
            tone="danger"
            title="Could not start the review"
            action={
              create.error instanceof ApiError && create.error.code === 'INSUFFICIENT_CREDITS' ? (
                <Link to="/app/credits" className={buttonClasses('primary', 'sm')}>
                  Add credits
                </Link>
              ) : undefined
            }
          >
            {create.error.message}
          </Notice>
        )}

        <div className="flex flex-col-reverse gap-3 border-t border-line pt-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-faint">
            Uses <span className="font-medium text-chalk-yellow">1 credit</span>
            {me ? ` — you have ${credits}.` : '.'} Only the changed code in this PR is sent for analysis.
          </p>
          <Button
            type="submit"
            variant="primary"
            disabled={outOfCredits}
            loading={create.isPending}
            icon={<Rocket className="h-4 w-4" />}
          >
            {create.isPending ? 'Starting…' : 'Start Review'}
          </Button>
        </div>
      </form>

      {config && config.samplePullRequests.length > 0 && (
        <section className="mt-8" aria-labelledby="samples-heading">
          <h2 id="samples-heading" className="flex items-center gap-2 text-sm font-semibold">
            <FlaskConical className="h-4 w-4 text-chalk-yellow" aria-hidden />
            Sample Pull Requests <span className="font-normal text-faint">— demo data, no credentials needed</span>
          </h2>
          <div className="mt-3 grid gap-2">
            {config.samplePullRequests.map((s, i) => (
              <button
                key={s.url}
                type="button"
                onClick={() => {
                  setPrUrl(s.url);
                  setTouched(true);
                }}
                className={cn(
                  'card flex flex-col items-start border-l-4 px-4 py-3 text-left transition-colors hover:bg-raised/70',
                  SAMPLE_STRIPES[i % SAMPLE_STRIPES.length],
                )}
              >
                <span className="text-sm font-medium">{s.title}</span>
                <span className="mt-0.5 font-mono text-xs text-muted">{s.url.replace('https://github.com/', '')}</span>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
